/**
 * Shared building blocks for the nine intelligence composites.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `apps/mcp-server` and `apps/mcp-stdio` each carried a full copy of
 * `intelligence.ts`. The copies were 675 changed lines apart, and every fix
 * measured against the live fleet landed on the Worker only. Three of those
 * fixes were still missing from the published npm package:
 *
 *   1. ACTIVE filter. OCS returns `status` as an ARRAY of records, so
 *      `String(row.status).toUpperCase() === "ACTIVE"` matches nothing and the
 *      fleet reads as empty. The Worker moved to `extractSubscriberStatus`;
 *      the CLI did not.
 *
 *   2. Usage window. `subscriberUsageOverPeriod` counts the range INCLUSIVELY
 *      and caps it at 7 days, so a 7-day offset asks for 8 and the call fails.
 *      `lastNDaysPeriod` in ocs-params.ts has expressed this correctly all
 *      along; not one intelligence call site on either surface used it.
 *
 *   3. Usage parsing. The response is a nested envelope, not a flat array, so
 *      an `Array.isArray(usage.data)` guard is never true and the whole block
 *      is skipped without an error to explain it. `extractDailyUsage` in
 *      usage.ts is the correct reader; the CLI never called it.
 *
 * Prose comments at each call site did not stop any of this — the same lesson
 * ocs-param-shapes.ts and list-subscriber.ts were written to record. One
 * shared implementation is the enforcement.
 */

import { extractSubscriberStatus } from "./list-subscriber.js";

/** Whole days from now until `dateStr`, rounded up. Negative once past. */
export function daysUntil(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/** Human-readable byte count for report tables. */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
}

/**
 * Is this subscriber row ACTIVE?
 *
 * Goes through {@link extractSubscriberStatus} because OCS nests the name
 * inside an array. Comparing the raw field against a string returns false for
 * every row, which reads as an empty fleet rather than as an error.
 */
export function isActiveSubscriber(row: unknown): boolean {
  return (extractSubscriberStatus(row) ?? "").toUpperCase() === "ACTIVE";
}

/** The ACTIVE rows of an aggregated subscriber list. */
export function filterActiveSubscribers<T>(rows: T[]): T[] {
  return rows.filter((row) => isActiveSubscriber(row));
}

/**
 * Unwrap `{subscriberList: [...]}` or accept a bare array.
 *
 * Distinct from `unwrapSubscriberList` in list-subscriber.ts, which reports
 * whether it recognised the shape so callers can pass an unknown payload
 * through untouched. Here an unrecognised payload contributes no rows, which
 * is what an aggregating fan-out wants.
 */
export function subscriberRows(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  const list = (raw as { subscriberList?: Record<string, unknown>[] } | null)?.subscriberList;
  return Array.isArray(list) ? list : [];
}

/**
 * Read the reseller's own balance from either OcsClient's direct payload or
 * the raw OCS response envelope. Money arrives as a decimal string in live
 * production, while staging fixtures use a number.
 */
export function extractResellerBalance(raw: unknown): number | null {
  const direct = raw as { balance?: unknown } | null;
  const enveloped = raw as { getResellerInfo?: { balance?: unknown } } | null;
  const value = direct?.balance ?? enveloped?.getResellerInfo?.balance;
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
